import { motion } from 'framer-motion';
import { Bell, AlertTriangle, Activity } from 'lucide-react';

interface SystemAlertsProps {
    overdueCount: number;
    activeWellsCount: number;
    totalWells: number;
    totalOil: number;
    isDarkMode: boolean;
}

export function SystemAlerts({ overdueCount, activeWellsCount, totalWells, totalOil, isDarkMode }: SystemAlertsProps) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 }}
            className={`
        rounded-xl shadow-sm p-[1px]
        ${isDarkMode ? 'bg-gradient-to-br from-red-500 to-rose-600' : 'bg-transparent border border-gray-200'}
      `}
        >
            <div className={`p-4 rounded-xl h-full ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <Bell className="h-5 w-5 mr-2 text-red-600" />
                    System Alerts
                </h3>
                <div className="space-y-3">
                    {overdueCount > 0 && (
                        <div className={`flex items-start space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-red-50 border-red-200'}`}>
                            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-900'}`}>Overdue Well Checks</p>
                                <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>{overdueCount} wells require immediate attention</p>
                            </div>
                        </div>
                    )}

                    {activeWellsCount < totalWells && (
                        <div className={`flex items-start space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-yellow-50 border-yellow-200'}`}>
                            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-900'}`}>Inactive Wells Detected</p>
                                <p className={`text-xs ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>{totalWells - activeWellsCount} wells are currently inactive</p>
                            </div>
                        </div>
                    )}

                    {totalOil === 0 && (
                        <div className={`flex items-start space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-blue-50 border-blue-200'}`}>
                            <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-900'}`}>No Production Data</p>
                                <p className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>Start adding production entries to see analytics</p>
                            </div>
                        </div>
                    )}

                    {overdueCount === 0 && activeWellsCount === totalWells && totalOil > 0 && (
                        <div className={`flex items-center space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-green-50 border-green-200'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-card' : 'bg-green-100'}`}>
                                <Activity className={`h-4 w-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                            </div>
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-900'}`}>All Systems Normal</p>
                                <p className={`text-xs ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>No alerts at this time</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
